<script setup>
import { reactive, computed, watch, inject } from "vue";
import { extractUniqueFilters } from "@/utils/utils.js";
import { useNavigation } from "@/composables/useNavigation";
import { InteractionPageEnum } from "@/components/InboxLeftMenu.utils.js";

import { useUserStore } from "@/stores/user";
import { useTeamStore } from "@/stores/team";
import { useTicketPriority } from "@/stores/ticketPriority";
import IconClose from "@/components/icons/IconClose.vue";

/** Filters hidden per inbox category. Add more categories or filter keys as needed. */
const HIDDEN_FILTER_KEYS_BY_CATEGORY = {
  [InteractionPageEnum.VA_INTERACTIONS]: [
    "user_responded_time",
    "ticket_created_time",
    "ticket_closed_time",
  ],
};

const emit = defineEmits([
  "toggle-filters-menu-visibility",
  "update-active-filters-count",
]);
const props = defineProps({
  isFiltersMenuVisible: {
    type: Boolean,
    default: false,
  },
  showTicketOwner: {
    type: Boolean,
    default: true,
  },
});

const currentCategory = inject("INBOX_CATEGORY");

const isFilterVisible = (filterKey) => {
  const category = currentCategory?.value;
  if (!category) return true;
  const hidden = HIDDEN_FILTER_KEYS_BY_CATEGORY[category];
  return !hidden || !hidden.includes(filterKey);
};

const userStore = useUserStore();
const teamsStore = useTeamStore();
const ticketPriorityStore = useTicketPriority();
const { queryParams, putQueryParams } = useNavigation();

const ticketPriorityOptions = computed(() =>
  ticketPriorityStore.allArray.map(tp => ({
    text: tp.name,
    value: tp._id
  }))
)

// const accessChannelsOptions = [
//   { value: "email", text: "Email" },
//   { value: "sms", text: "SMS" },
//   { value: "WhatsApp", text: "WhatsApp" },
//   { value: "slack", text: "Slack" },
//   { value: "meta", text: "Meta" },
//   { value: "msteams", text: "Ms Teams" },
//   { value: "wordpress", text: "Wordpress" },
//   { value: "web", text: "Web" },
// ];

const SIDEBAR_FILTER_PREFIX = "sidebar_filter_";

const getPrefixedKey = (key) => `${SIDEBAR_FILTER_PREFIX}${key}`;

const isFilterEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

const parseQueryValue = (value, isDate = false) => {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) {
    return value.length > 0 ? value : null;
  }
  if (typeof value === "string") {
    if (isDate) {
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return value;
  }
  return value;
};

const readInitialFiltersFromQuery = () => {
  const q = queryParams.value || {};
  return {
    // access_channel_status_text: parseQueryValue(q[getPrefixedKey("access_channel_status_text")]),
    user_full_name: parseQueryValue(q[getPrefixedKey("user_full_name")]),
    assignee: parseQueryValue(q[getPrefixedKey("assignee")]),
    dt_open_start_date: parseQueryValue(q[getPrefixedKey("dt_open_start_date")], true),
    dt_open_end_date: parseQueryValue(q[getPrefixedKey("dt_open_end_date")], true),
    created_start_date: parseQueryValue(q[getPrefixedKey("created_start_date")], true),
    created_end_date: parseQueryValue(q[getPrefixedKey("created_end_date")], true),
    last_modified_start_date: parseQueryValue(q[getPrefixedKey("last_modified_start_date")], true),
    last_modified_end_date: parseQueryValue(q[getPrefixedKey("last_modified_end_date")], true),
    dt_closed_start_date: parseQueryValue(q[getPrefixedKey("dt_closed_start_date")], true),
    dt_closed_end_date: parseQueryValue(q[getPrefixedKey("dt_closed_end_date")], true),
    dt_user_responded_start_date: parseQueryValue(q[getPrefixedKey("dt_user_responded_start_date")], true),
    dt_user_responded_end_date: parseQueryValue(q[getPrefixedKey("dt_user_responded_end_date")], true),
    user_id: parseQueryValue(q[getPrefixedKey("user_id")]),
    // last_modify_by: parseQueryValue(q[getPrefixedKey("last_modify_by")]),
    // unit_name: parseQueryValue(q[getPrefixedKey("unit_name")]),
    team: parseQueryValue(q[getPrefixedKey("team")]),
    company_id: parseQueryValue(q[getPrefixedKey("company_id")]),
    ticket_priority: parseQueryValue(q[getPrefixedKey("ticket_priority")]),
  };
};

const filters = reactive({
  // access_channel_status_text: null,
  user_full_name: null,
  assignee: null,
  dt_open_start_date: null,
  dt_open_end_date: null,
  created_start_date: null,
  created_end_date: null,
  last_modified_start_date: null,
  last_modified_end_date: null,
  dt_closed_start_date: null,
  dt_closed_end_date: null,
  dt_user_responded_start_date: null,
  dt_user_responded_end_date: null,
  user_id: null,
  team: null,
  company_id: null,
  ticket_priority: null,
  ...readInitialFiltersFromQuery(),
});

const syncSidebarFiltersFromQuery = () => {
  const initial = readInitialFiltersFromQuery();
  Object.entries(initial).forEach(([key, value]) => {
    filters[key] = value;
  });
};

/**
 * Keep the sidebar UI in-sync with the URL.
 * This prevents "stale" selected filters when navigation clears `sidebar_filter_*` params.
 *
 * Note: we only react to changes to `sidebar_filter_*` keys so we don't clobber unsaved edits
 * when unrelated query params change (e.g. paging/sorting).
 */
watch(
  () => {
    const q = queryParams.value || {};
    const sidebarOnly = Object.fromEntries(
      Object.entries(q).filter(([k]) => k.startsWith(SIDEBAR_FILTER_PREFIX))
    );
    return JSON.stringify(sidebarOnly);
  },
  () => {
    syncSidebarFiltersFromQuery();
  },
  { immediate: true }
);

const activeFiltersCount = computed(() => {
  const extracted = extractUniqueFilters(filters);
  const count = Object.values(extracted).filter((v) => !isFilterEmpty(v)).length;
  emit("update-active-filters-count", count);
  return count;
});

const updateInteractionCreatedTime = (val) => {
  if (!val) {
    filters.created_start_date = null;
    filters.created_end_date = null;
    return;
  }
  const [start, end] = val;
  filters.created_start_date = start;
  filters.created_end_date = end;
};

const updateTicketCreatedTime = (val) => {
  if (!val) {
    filters.dt_open_start_date = null;
    filters.dt_open_end_date = null;
    return;
  }
  const [start, end] = val;
  filters.dt_open_start_date = start;
  filters.dt_open_end_date = end;
};

const updateLastModifyTime = (val) => {
  if (!val) {
    filters.last_modified_start_date = null;
    filters.last_modified_end_date = null;
    return;
  }
  const [start, end] = val;
  filters.last_modified_start_date = start;
  filters.last_modified_end_date = end;
};

const updateTicketClosedTime = (val) => {
  if (!val) {
    filters.dt_closed_start_date = null;
    filters.dt_closed_end_date = null;
    return;
  }
  const [start, end] = val;
  filters.dt_closed_start_date = start;
  filters.dt_closed_end_date = end;
};

const updateUserRespondedTime = (val) => {
  if (!val) {
    filters.dt_user_responded_start_date = null;
    filters.dt_user_responded_end_date = null;
    return;
  }
  const [start, end] = val;
  filters.dt_user_responded_start_date = start;
  filters.dt_user_responded_end_date = end;
};

const applyFiltersToQuery = () => {
  const nextQuery = { ...(queryParams.value || {}) };

  Object.keys(nextQuery).forEach((k) => {
    if (k.startsWith(SIDEBAR_FILTER_PREFIX)) delete nextQuery[k];
  });

  Object.entries(filters).forEach(([key, value]) => {
    if (isFilterEmpty(value)) return;
    nextQuery[getPrefixedKey(key)] = value;
  });

  putQueryParams(nextQuery);
};

const onApplyFilters = () => {
  applyFiltersToQuery();
  emit("toggle-filters-menu-visibility");
};

const clearAllFilters = () => {
  Object.keys(filters).forEach((key) => {
    filters[key] = null;
  });

  const nextQuery = { ...(queryParams.value || {}) };
  Object.keys(nextQuery).forEach((k) => {
    if (k.startsWith(SIDEBAR_FILTER_PREFIX)) delete nextQuery[k];
  });
  putQueryParams(nextQuery);

  emit("toggle-filters-menu-visibility");
};

watch(
  () => props.showTicketOwner,
  (newValue) => {
    if (!newValue) {
      filters.assignee = null;
    }
  }
);
</script>

<template>
  <b-offcanvas
    class="filters-sidebar"
    placement="end"
    id="sidebar-1"
    title="Sidebar"
    no-close-on-esc
    teleport-disabled
    :model-value="isFiltersMenuVisible"
    @update:model-value="(v) => !v && $emit('toggle-filters-menu-visibility')"
  >
    <template #header>
      <div class="w-100 d-flex justify-content-between align-items-center px-1">
        <span class="fw-semibold text-neutral-600 h6 m-0"> Filters</span>

        <IconClose
          class="cursor-pointer"
          @click="$emit('toggle-filters-menu-visibility')"
        />
      </div>
    </template>

    <!-- <div class="filter-item">
      <Collapsible label="Priority" :collapsed="Boolean(filters.priority)">
        <SelectInput
          class="mt-2"
          :options="priorityOptions"
          :tryToFindNameInOptionsOnFirstLoad="true"
          :reduce="(option) => option.value"
          v-model="filters.priority"
        />
      </Collapsible>
    </div> -->

    <div class="filter-item">
      <Collapsible
        label="Thread time"
        :collapsed="Boolean(filters.created_start_date)"
      >
        <CustomDatePicker
          class="mt-2"
          range
          :model-value="
            filters.created_start_date
              ? [filters.created_start_date, filters.created_end_date]
              : null
          "
          @update:model-value="updateInteractionCreatedTime"
        />
      </Collapsible>
    </div>

    <div class="filter-item" v-if="isFilterVisible('user_responded_time')">
      <Collapsible
        label="User responded time"
        :collapsed="Boolean(filters.dt_user_responded_start_date)"
      >
        <CustomDatePicker
          class="mt-2"
          range
          :model-value="
            filters.dt_user_responded_start_date
              ? [
                  filters.dt_user_responded_start_date,
                  filters.dt_user_responded_end_date,
                ]
              : null
          "
          @update:model-value="updateUserRespondedTime"
        />
      </Collapsible>
    </div>

    <!-- this is now the same as created by... we should just merge them into created by user or something?-->
    <div class="filter-item">
      <Collapsible
        label="Ticket creator"
        :collapsed="Boolean(filters.user_id)"
      >
        <SelectInput
          class="mt-2"
          :options="userStore.allUsersDropdownOptions"
          :tryToFindNameInOptionsOnFirstLoad="true"
          :reduce="(option) => option.value"
          v-model="filters.user_id"
          name="user_id"
        />
      </Collapsible>
    </div>

    <div class="filter-item" v-if="showTicketOwner">
      <Collapsible label="Assignees" :collapsed="Boolean(filters.assignee)">
        <SelectInput
          class="mt-2"
          :multiple="true"
          :options="userStore.allUsersDropdownOptions"
          :tryToFindNameInOptionsOnFirstLoad="true"
          :reduce="(option) => option.value"
          v-model="filters.assignee"
        />
      </Collapsible>
    </div>
    <!-- property DNE on Thread model -->
    <!-- <div class="filter-item">
      <Collapsible
        label="Last modified by"
        :collapsed="Boolean(filters.last_modify_by)"
      >
        <SelectInput
          class="mt-2"
          :options="userStore.allUsersDropdownOptions"
          :tryToFindNameInOptionsOnFirstLoad="true"
          :reduce="(option) => option.value"
          v-model="filters.last_modify_by"
          name="last_modify_by"
        />
      </Collapsible>
    </div> -->

    <div class="filter-item" v-if="isFilterVisible('ticket_created_time')">
      <Collapsible
        label="Ticket created time"
        :collapsed="Boolean(filters.dt_open_start_date)"
      >
        <CustomDatePicker
          class="mt-2"
          range
          :model-value="
            filters.dt_open_start_date
              ? [filters.dt_open_start_date, filters.dt_open_end_date]
              : null
          "
          @update:model-value="updateTicketCreatedTime"
        />
      </Collapsible>
    </div>
  
    <div class="filter-item" v-if="isFilterVisible('ticket_closed_time')">
      <Collapsible
        label="Ticket closed time"
        :collapsed="Boolean(filters.dt_closed_start_date)"
      >
        <CustomDatePicker
          class="mt-2"
          :model-value="
            filters.dt_closed_start_date
              ? [filters.dt_closed_start_date, filters.dt_closed_end_date]
              : null
          "
          range
          @update:model-value="updateTicketClosedTime"
        />
      </Collapsible>
    </div>

    <div class="filter-item">
      <Collapsible
        label="Last modify time"
        :collapsed="Boolean(filters.last_modified_start_date)"
      >
        <CustomDatePicker
          class="mt-2"
          :model-value="
            filters.last_modified_start_date
              ? [
                  filters.last_modified_start_date,
                  filters.last_modified_end_date,
                ]
              : null
          "
          range
          @update:model-value="updateLastModifyTime"
        />
      </Collapsible>
    </div>
    <!-- property DNE on Thread model -->
    <!-- <div class="filter-item">
      <Collapsible
        label="Account unit name"
        :collapsed="Boolean(filters.unit_name)"
      >
        <SelectInput
          class="mt-2"
          :options="unitStore.dropdownOptions"
          :tryToFindNameInOptionsOnFirstLoad="true"
          :reduce="(option) => option.value"
          v-model="filters.unit_name"
          name="unit_name"
        />
      </Collapsible>
    </div> -->

    <!-- property DNE on Thread model but shoudl be added -->
    <!-- <div class="filter-item">
      <Collapsible
        label="Access channel"
        :collapsed="Boolean(filters.access_channel_status_text)"
      >
        <SelectInput
          class="mt-2"
          :multiple="true"
          :options="accessChannelsOptions"
          :tryToFindNameInOptionsOnFirstLoad="true"
          :reduce="(option) => option.value"
          v-model="filters.access_channel_status_text"
        />
      </Collapsible>
    </div> -->

    <div class="filter-item">
      <Collapsible label="Team" :collapsed="Boolean(filters.team)">
        <SelectInput
          class="mt-2"
          :multiple="true"
          :options="teamsStore.dropdownOptions"
          :tryToFindNameInOptionsOnFirstLoad="true"
          :reduce="(option) => option.value"
          v-model="filters.team"
        />
      </Collapsible>
    </div>

    <div class="filter-item">
      <Collapsible label="Ticket Priority" :collapsed="Boolean(filters.ticket_priority)">
        <SelectInput
          class="mt-2"
          :multiple="true"
          :options="ticketPriorityOptions"
          :tryToFindNameInOptionsOnFirstLoad="true"
          :reduce="(option) => option.value"
          v-model="filters.ticket_priority"
        />
      </Collapsible>
    </div>

    <!-- <div v-if="userStore.canSeeAllOrgs" class="filter-item">
      <Collapsible
        class="mt-2"
        label="Organization"
        :collapsed="Boolean(filters.company_id)"
      >
        <SelectInput
          :options="organizationStoreAllOrganizationsMappedOptions"
          :tryToFindNameInOptionsOnFirstLoad="true"
          :reduce="(option) => option.value"
          v-model="filters.company_id"
          name="company_id"
        />
      </Collapsible>
    </div> -->

    <template #footer>
      <div class="footer d-flex justify-content-between align-items-center">
        <custom-button
          @click="clearAllFilters"
          :disabled="!activeFiltersCount"
          class="me-2 font-size-xs"
          button-type="transparent"
          has-font-semi-bold
        >
          Clear All Filters
        </custom-button>
        <custom-button
          @click="onApplyFilters"
          class="me-2 font-size-xs"
          has-font-semi-bold
          button-type="blue"
        >
          Apply Filters
          <span v-if="activeFiltersCount">({{ activeFiltersCount }})</span>
        </custom-button>
      </div>
    </template>
  </b-offcanvas>
</template>

<style lang="scss">
.filters-sidebar {
  max-width: 21.25rem;

  .offcanvas-header {
    padding: 1rem 1.5rem;
    border-bottom: 2px solid $neutral-150;
  }

  .offcanvas-body {
    padding: 0;
  }

  .close-btn-wrapper {
    cursor: pointer;
  }
}

.offcanvas-backdrop {
  background-color: transparent;
}

.footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid $neutral-150;
}

.filter-item {
  padding: 1.5rem;
  border-bottom: 1px solid $neutral-150;

  &:hover {
    background: $neutral-50;
  }
}
</style>

<style scoped lang="scss">
:deep(.btn) {
  &.transparent {
    border: none;
  }
}
</style>
